import * as React from 'react';
import { CheckboxList } from '@grandvisionhq/common';
import { useLink } from '@grandvisionhq/frk-routing';
import { useIntl, useSelector, statusFailed } from '@grandvisionhq/state';
import { useScrollIntoView } from '@grandvisionhq/utils';
import EmailSentConfirmation from '../../components/EmailSentConfirmation/EmailSentConfirmation.js';
import { logCreateAccountEvent } from '../../data-layer/create-account.js';
import { useAdditionalLoginProviders } from '../../hooks/use-additional-login-providers.js';
import { useSocialLoginFlow } from '../../hooks/use-social-login-flow.js';
import { getAuth } from '../../selectors.js';
import { isUninitialized } from '../../state.js';
import { useCreateAccount } from '../hooks/use-create-account.js';
import SignUp from './SignUp.js';
import { useAvailableOptIns } from '../../OptIns/hooks/use-available-opt-ins.js';

const SignUpContainer = ({ termsRichText  })=>{
    const { hasProviders  } = useAdditionalLoginProviders();
    const { getLabel , hasLabel , getFormattedLabel  } = useIntl();
    const auth = useSelector(getAuth);
    const { availableOptIns  } = useAvailableOptIns();
    const availableOptInsForSignUp = availableOptIns.filter((o)=>o.isInSignup
    );
    const [createAccount] = useCreateAccount();
    const { protocol , host  } = window.location;
    const { href: callbackHref  } = useLink({
        route: 'auth.callback'
    }) || {};
    const loginRedirectUrl = `${protocol}//${host}${callbackHref}`;
    const { elementRef , triggerScroll  } = useScrollIntoView({
        block: 'center'
    });
    const { initializeSocialLoginFlow  } = useSocialLoginFlow();
    const labels = {
        error: getLabel('account.createAccount.signup.errors.general'),
        dataConsent: hasLabel('account.consent.dataProcessing') ? getFormattedLabel('account.consent.dataProcessing', {
            parseLink: true
        }) : undefined,
        or: getLabel(`account.signup.general.or`),
        emailSignup: getLabel(`account.createAccount.signup.title.emailSignup`),
        hasAccount: {
            title: getLabel('account.hasAccount.title'),
            text: getLabel('account.hasAccount.text')
        },
        signup: {
            title: getLabel('account.signup.title')
        },
        actions: {
            login: getLabel('account.action.login'),
            createAccount: getLabel('account.action.createAccount')
        },
        social: {
            googleButton: getLabel('account.signup.social.google'),
            consent: getLabel('account.signup.social.consent')
        }
    };
    const [userData, setUserData] = React.useState(()=>({
            email: ''
        })
    );
    const [hasSignedUp, setHasSignedUp] = React.useState(false);
    const [selectedOptIns, setSelectedOptIns] = React.useState([]);
    const optIns = React.useMemo(()=>{
        if (!availableOptInsForSignUp || availableOptInsForSignUp.length === 0) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(CheckboxList, {
            idPrefix: "opt-in__",
            items: availableOptInsForSignUp,
            onChange: setSelectedOptIns,
            selectedItems: selectedOptIns,
            getItemIdentifier: (optIn)=>optIn.titleKey
            ,
            renderItem: (item)=>/*#__PURE__*/ React.createElement("span", null, getFormattedLabel(item.titleKey, {
                    parseHTML: true
                }))
        });
    }, [
        availableOptInsForSignUp,
        getFormattedLabel,
        selectedOptIns
    ]);
    const signUp = React.useCallback(()=>{
        createAccount({
            email: userData.email,
            availableOptIns: availableOptInsForSignUp,
            selectedOptIns,
            personalDetails: userData.personalDetails,
            loginRedirectUrl
        });
    }, [
        selectedOptIns,
        userData.email,
        userData.personalDetails
    ]);
    const handleSubmit = (email, personalDetails)=>{
        setUserData({
            email,
            personalDetails
        });
        setHasSignedUp(true);
        setTimeout(()=>{
            triggerScroll();
        }, 50);
    };
    const onResend = ()=>{
        logCreateAccountEvent({
            type: 'create_account_resend_click'
        });
        setUserData({
            email: ''
        });
        setSelectedOptIns([]);
        setHasSignedUp(false);
    };
    React.useEffect(()=>{
        if (userData.email) {
            signUp();
        }
    }, [
        signUp,
        userData
    ]);
    if (isUninitialized(auth)) {
        throw new Error(`Should not render SignUp page when auth is not initialized`);
    }
    if (hasSignedUp) {
        const error = statusFailed(auth.signupCall) ? new Error(getLabel('account.createAccount.signup.errors.general')) : undefined;
        return /*#__PURE__*/ React.createElement(EmailSentConfirmation, {
            error: error,
            email: userData.email,
            onResend: onResend,
            title: getLabel('account.signupConfirmation.title'),
            firstParagraph: getLabel('account.signupConfirmation.firstParagraph'),
            secondParagraph: getLabel('account.signupConfirmation.secondParagraph'),
            resendLink: getLabel('account.action.resendLink'),
            successMessage: "account.signupConfirmation.resendEmailSuccess",
            ref: elementRef
        });
    }
    return /*#__PURE__*/ React.createElement(SignUp, {
        labels: labels,
        optIns: optIns,
        onSocialLogin: initializeSocialLoginFlow,
        onFormSubmit: handleSubmit,
        termsRichText: termsRichText,
        hasProviders: hasProviders
    });
};
var SignUpContainer$1 = SignUpContainer;

export { SignUpContainer, SignUpContainer$1 as default };
//# sourceMappingURL=SignUpContainer.js.map
